import React, { useEffect } from 'react';
import { theme } from '../../theme';
import { useMessageGetter } from 'react-message-context';
import Card from '../../components/common/Card';
import styled from 'styled-components/macro';
import { NotificationType, useApp, useAppDispatch } from '../../context/appContext';
import { getMenuItemPathBySlug } from '../../utils/wordpress';
import { Slugs } from '../../constants/knownSlugs';
import { FlexColumn, FlexRow } from '../../components/basic/Flex';
import { LinkButton } from '../../components/common';
import { useKennoTenant } from '../../context/kennoTenantContext';
import { loadBillingHistory } from '../../context/kennoTenantActions';
import { LOAD_STATES } from '../../context/common';
import { showNotification } from '../../context/appActions';
import Loader from '../../components/basic/Loader';
import { formatDate, formatEuros, shouldHideFinancialInfo } from '../../utils';
import { MaintenanceNote } from '../../components/FinancialInfoMaintenanceNote';

const Cell = styled(FlexColumn)`
  width: 50%;
  height: min-content;
`;
const CellFull = styled(Cell)`
  width: 100%;
  margin-top: 20px;
`;
const CellTitle = styled.p`
  font-size: 16px;
  margin-bottom: 12px;
  font-family: ${(props) => props.theme.text.primaryFont};
`;
const BoldCellTitle = styled(CellTitle)`
  font-weight: 700;
`;
const CellValue = styled.p`
  font-size: 21px;
  margin: 0;
`;

const RentCard = () => {
  const t = useMessageGetter('home.grid.rent');
  const [{ billingHistory }, dispatch] = useKennoTenant();
  const [{ menu }] = useApp();
  const appDispatch = useAppDispatch();

  useEffect(() => {
    loadBillingHistory(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (billingHistory.loadState === LOAD_STATES.ERROR) {
      showNotification(appDispatch, t('loadfailure'), NotificationType.ERROR);
    }
  }, [billingHistory.loadState]); // eslint-disable-line react-hooks/exhaustive-deps

  const cardContent = () => {
    if (shouldHideFinancialInfo()) {
      return <MaintenanceNote translationKey="financial.rentCard" />;
    }
    if (billingHistory.loadState !== LOAD_STATES.READY) {
      return <Loader />;
    }
    const currentBillingMonth = billingHistory.items?.find((item) => item.isCurrent);
    const billedThisMonth = billingHistory.items?.find((item) => item.isCurrent)?.billed;
    const currentTotalPaid = currentBillingMonth?.sumAfter;
    const hasPaidInAdvance = currentTotalPaid && currentTotalPaid > 0;

    return (
      <FlexRow wrap="wrap">
        {billedThisMonth?.map((bill) => (
          <>
            <Cell>
              <CellTitle>{bill.reasonName}</CellTitle>
              <CellValue>{formatEuros(bill.amountEuros)}</CellValue>
            </Cell>
            <Cell>
              <CellTitle>{t('due_date_label')}</CellTitle>
              <CellValue>{formatDate(bill.dueDate)}</CellValue>
            </Cell>
          </>
        ))}
        {hasPaidInAdvance && (
          <CellFull>
            <BoldCellTitle>{t('paid_in_advance')}</BoldCellTitle>
            <CellValue>{`+${currentTotalPaid}`}</CellValue>
          </CellFull>
        )}
      </FlexRow>
    );
  };

  return (
    <Card
      title={t('title')}
      background={theme().colors.mint}
      mainButton={
        <LinkButton
          size="small"
          background="transparent"
          to={getMenuItemPathBySlug(menu.items, Slugs.PAYMENT)}
          iconRight
          icon="arrowForward"
        >
          {t('action.label')}
        </LinkButton>
      }
      secondaryButton={
        <LinkButton size="small" to={getMenuItemPathBySlug(menu.items, Slugs.BILLING)} primary>
          {t('action2.label')}
        </LinkButton>
      }
    >
      {cardContent()}
    </Card>
  );
};

export default RentCard;
